/**
 * Auth-core's half of the centralized commit-time identity reconcile: the predicate that recognizes a COMMITTED
 * identity change, so every enrolled RTK Query endpoint can be reconciled per its declared policy (purge /
 * revalidate / account-invariant). Centralized on purpose: no feature can forget to purge its own user-scoped
 * cache, and no feature has to reimplement the A-vs-B distinction a boolean auth read cannot make.
 *
 * The LISTENER is not here — it is `armIdentityCommitReconcile` in `@pressreader/appstore`'s `identity.policy`,
 * armed off the first `declareIdentityPolicy`. All it needs from this lib is this predicate, injected at auth
 * bootstrap (`./index.ts`, `setIdentityCommitPredicate`), because recognizing a commit means reading the auth
 * slice and appstore cannot import it.
 *
 * ITS OWN MODULE, deliberately. The injection is EAGER (it happens in the auth bootstrap every consumer of the
 * auth slice goes through), so everything reachable from here lands on the eager path. `./auth.reconcile.ts` is
 * the opposite — lazy, pulled in by a feature, and it imports appstore's policy module — so keeping the predicate
 * out of it is what keeps the eager cost down to the selectors and the action creator, all of which are already
 * on the eager path anyway.
 */

import type { UnknownAction } from 'redux';

import { type IdentityCommitPredicate, selectFrom } from '@pressreader/appstore';

import { AUTHENTICATION_CORE_MODULE_NAME } from '../constants';

import { identityReconcileRequested } from './actions/identity.internal';
import { selectUser } from './selectors/common';

/**
 * The auth slice straight off the root state — a REFERENCE read, deliberately not a selector, so the common
 * "nothing in auth changed" case can be settled before any selector runs (see the hot-path note below).
 */
function authSlice(state: unknown) {
    return (state as Record<string, unknown> | undefined)?.[AUTHENTICATION_CORE_MODULE_NAME];
}

/**
 * The identity every RTK Query cache entry was filled under. `selectUser` carries a stable anonymous sentinel
 * (`createAnonymousUser`, `./utils`), so anonymous is simply another id and a plain `!==` detects login, logout
 * AND an authenticated A→B account switch — the last of which `selectUserIsAuthenticated` structurally cannot
 * see (true → true).
 *
 * Defensive on purpose: the predicate below runs on EVERY dispatched action, and the listener middleware
 * reports (and swallows) a predicate throw once per action, so a store without the auth slice would turn into
 * log spam rather than a visible failure.
 */
function readIdentityId(state: unknown): string | undefined {
    if (!authSlice(state)) {
        return undefined;
    }

    return selectFrom(state, selectUser)?.id;
}

/**
 * Two arms, one reconcile. The STATE-DIFF arm covers every real commit — the reconcile needs the new identity to
 * be the one a refetch would run under, which is commit time, not token time (the instant signal for the token
 * change itself is the `identityTokenChanged` action). The listener middleware hands the predicate the state from
 * before AND after the action was reduced, so the transition is read straight off that pair — no module-level
 * "last identity" to go stale. It therefore fires on the action that actually replaced the user
 * (`authInitialized`, `loginDone`, `signUpDone`, `logOutDone`), which is at or before the `userChangeDone` that
 * feature reconcile listeners react to (the auth saga puts it afterwards). The ACTION arm covers the transitions
 * that never commit and so have no such pair to read: the auth saga's mismatch recheck relays those as
 * `identityReconcileRequested`, since it is eager and cannot name the lazily-loaded policy machinery.
 *
 * HOT PATH: this runs for every action dispatched in the app. The auth slice's reference changes if and only if
 * one of its reducers wrote, so the reference compare short-circuits effectively every action into two property
 * reads and no selector call at all. Without it, reselect's `weakMapMemoize` would allocate a cache node per new
 * root state — twice per action — to answer a question that is almost always "no".
 */
export const identityCommitPredicate: IdentityCommitPredicate = (action, currentState, originalState) => {
    // `IdentityCommitPredicate` types the action `unknown` (appstore knows nothing about this lib's actions), and
    // `match` only ever reads `.type`, which every dispatched action has.
    if (identityReconcileRequested.match(action as UnknownAction)) {
        return true;
    }

    const current = authSlice(currentState);
    const original = authSlice(originalState);

    return current !== original && readIdentityId(currentState) !== readIdentityId(originalState);
};
